import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BusinessModeService } from '@core/services/business-mode.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavComponent } from '@shared/components/nav/nav.component';
import { LanguageService } from '@core/services/language.service';
import { ThemeService } from '@core/services/theme.service';
import { AuthService } from '@features/auth/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { PipTimerService } from '@core/services/pip-timer.service';
import { DesignToggleService } from '@core/services/design-toggle.service';
import { AcquisitionCaptureService } from '@core/services/acquisition-capture.service';

const TITLE_MAP: Record<string, string> = {
  '/dashboard':          'nav.dashboard',
  '/clients':            'nav.clients',
  '/projects':           'nav.projects',
  '/tasks':              'nav.tasks',
  '/finance/time-tracking': 'nav.timeTracking',
  '/finance/invoices':   'nav.invoices',
  '/finance/receipts':   'nav.receipts',
  '/finance/transactions': 'nav.transactions',
  '/finance/reports':    'nav.reports',
  '/contracts':          'nav.contracts',
  '/profile':            'nav.profile',
  '/settings':           'nav.settings',
  '/business-settings':  'nav.businessSettings',
  '/pricing':            'nav.pricing',
  '/admin':              'nav.adminPanel',
  '/email':              'nav.email',
  '/login':              'nav.login',
};

/** Routes whose label changes per business mode (Cases / Work Orders /
 *  Customers). Anything absent keeps its generic TITLE_MAP label. */
const MODE_KEYS: Record<string, string> = {
  '/clients':  'clients',
  '/projects': 'projects',
  '/tasks':    'tasks',
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'EZatWorkApp';
  private businessModeService = inject(BusinessModeService);
  private languageService = inject(LanguageService);
  readonly themeService = inject(ThemeService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private titleService = inject(Title);
  private translate = inject(TranslateService);
  readonly isAuthenticated = this.authService.isAuthenticated;
  /**
   * Pages that belong to another product and must not appear inside EZ@Work's
   * shell even for someone signed in to EZ@Work: EZ Type's connect page. Read
   * from the address before the first paint, then on every navigation.
   */
  readonly standalonePage = signal(AppComponent.isStandalone(window.location.pathname));
  readonly pipTimerSvc = inject(PipTimerService);
  // Inject so the service constructor runs at app bootstrap and reads the
  // `?design=v2` URL param / localStorage before the first paint. `readonly`
  // so the template can branch shell rendering on designToggle.version().
  readonly designToggle = inject(DesignToggleService);
  private acquisitionCapture = inject(AcquisitionCaptureService);
  @ViewChild(NavComponent) nav?: NavComponent;

  constructor() {}

  private static isStandalone(url: string): boolean {
    return url === '/eztype' || url.startsWith('/eztype/') || url.startsWith('/eztype?');
  }

  ngOnInit(): void {
    this.acquisitionCapture.captureOnce();

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.standalonePage.set(AppComponent.isStandalone(this.router.url));
      this.updateTitle(this.router.url);
    });

    this.translate.onLangChange.subscribe(() => this.updateTitle(this.router.url));
  }

  private updateTitle(url: string): void {
    const key = Object.keys(TITLE_MAP).find(k => url === k || url.startsWith(k + '/') || url.startsWith(k + '?'));
    // Remap to the per-mode term so the tab reads "תיקים" in a legal
    // workspace rather than the generic "פרויקטים" the sidebar already drops.
    const labelKey = key
      ? this.businessModeService.getNavLabel(MODE_KEYS[key] ?? '', TITLE_MAP[key])
      : null;
    const screenName = labelKey ? this.translate.instant(labelKey) : null;
    this.titleService.setTitle(screenName ? `${screenName} | EZ@Work` : 'EZ@Work');
  }

  toggleMobileNav(): void { this.nav?.toggleMobileMenu(); }
  closeMobileNav(): void  { this.nav?.closeMobileMenu(); }
  get mobileNavOpen(): boolean { return this.nav?.isMobileMenuOpen ?? false; }
}
